/**
 * Shared wording and formatting for the Activity tab and the Data Health
 * report.
 */
import type { ActivityStatus, ActivityThresholds, ActorKind, OrganizationActivitySummary } from '../../api/activityLog';

/** The API returns UTC datetimes without a zone; read them as UTC. */
export const parseServerDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const hasZone = /([zZ]|[+-]\d\d:?\d\d)$/.test(value);
  const parsed = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;

/** "3 days ago", "2 months ago", or `never` when there's no date */
export const formatRelative = (value: string | null | undefined, now: Date = new Date(), never = 'Never'): string => {
  const date = parseServerDate(value);
  if (!date) return never;
  const seconds = Math.max(0, (now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return plural(minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return plural(hours, 'hour');
  const days = Math.floor(hours / 24);
  if (days < 30) return plural(days, 'day');
  if (days < 365) return plural(Math.min(11, Math.max(1, Math.floor(days / 30.44))), 'month');
  return plural(Math.max(1, Math.floor(days / 365.25)), 'year');
};

export const formatDateTime = (value: string | null | undefined): string => {
  const date = parseServerDate(value);
  return date ? date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
};

export const STATUS_META: Record<ActivityStatus, { label: string; className: string; rank: number }> = {
  current: { label: 'Current', rank: 0, className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  aging: { label: 'Aging', rank: 1, className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  stale: { label: 'Stale', rank: 2, className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  never: { label: 'No edits yet', rank: 3, className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
};

/** What each status means, for legends */
export const statusLegend = (t: ActivityThresholds): Array<[ActivityStatus, string]> => [
  ['current', `The organization's own users edited within ${t.currentWithinDays} days`],
  ['aging', `Last edit by its own users was ${t.currentWithinDays}–${t.agingWithinDays} days ago`],
  ['stale', `No edit by its own users in over ${t.agingWithinDays} days`],
  ['never', 'Its own users have never edited'],
];

/** "District staff" on a district, "Organization staff" elsewhere */
export const organizationStaffLabel = (leaType: string | null | undefined): string =>
  leaType === 'district' ? 'District staff' : 'Organization staff';

export const actorKindLabel = (kind: ActorKind, leaType: string | null | undefined): string => {
  if (kind === 'aiu') return 'AIU staff';
  if (kind === 'system') return 'System';
  return organizationStaffLabel(leaType);
};

export const ACTOR_KIND_CLASSES: Record<ActorKind, string> = {
  organization: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  aiu: 'bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  system: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export const calendarLabel = (summary: Pick<OrganizationActivitySummary, 'calendarStatus' | 'calendarYear'>): string => {
  if (!summary.calendarStatus) return 'n/a';
  const status = { complete: 'Complete', partial: 'Partly filled in', missing: 'Not started' }[summary.calendarStatus];
  return summary.calendarYear ? `${status} (${summary.calendarYear})` : status;
};

export const ROLE_LABELS: Record<string, string> = {
  superAdmin: 'Super Admin',
  organizationAdmin: 'Org Admin',
  viewer: 'Viewer',
};

export const WINDOW_OPTIONS = [
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 365, label: 'Last 12 months' },
];
